// Centralized date & timezone helpers using Moment.js with timezone support
// NOTE: Requires moment-timezone package to be installed
// npm install moment moment-timezone

import moment from 'moment-timezone'

export const DEFAULT_CANADA_TZ = process.env.NEXT_PUBLIC_TIMEZONE || "";  

export interface TimeZoneDateParts {
  year: number
  monthIndex: number // 0-11
  day: number
}

export interface TimeZoneInfo {
  timeZone: string
  year: number
  monthIndex: number // 0-11  
  day: number
  hour: number
  minute: number
  second: number
  millisecond: number
  utcOffset: number // offset in minutes
  offsetString: string // e.g. "-05:00"
  abbreviation: string // e.g. "EST" or "EDT"
  formatted: string // ISO format with offset
  display: string // human readable
  dateKey: number // YYYYMMDD for comparisons
}

/**
 * Get current time in any IANA timezone using Moment.js
 * @param timeZone - IANA timezone identifier (e.g., 'America/Toronto', 'Europe/London')
 * @returns Complete timezone information object
 */
export const getTimeInTimeZone = (timeZone: string = DEFAULT_CANADA_TZ): TimeZoneInfo => {
  try {
    const momentTz = moment().tz(timeZone)

    return {
      timeZone,
      year: momentTz.year(),
      monthIndex: momentTz.month(), // moment uses 0-11
      day: momentTz.date(),
      hour: momentTz.hour(),
      minute: momentTz.minute(), 
      second: momentTz.second(),
      millisecond: momentTz.millisecond(),
      utcOffset: momentTz.utcOffset(), // in minutes
      offsetString: momentTz.format('Z'), // e.g. "-05:00"
      abbreviation: momentTz.format('z'), // e.g. "EST"
      formatted: momentTz.format(), // ISO with offset
      display: `${momentTz.format('YYYY-MM-DD HH:mm:ss')} ${momentTz.format('Z')} (${momentTz.format('z')}) - ${timeZone}`,
      dateKey: dateKey(momentTz.year(), momentTz.month(), momentTz.date())
    }
  } catch (error) {

    // Fallback to local time
    const localMoment = moment()
    return {
      timeZone: 'Local (fallback)',
      year: localMoment.year(),
      monthIndex: localMoment.month(),
      day: localMoment.date(),
      hour: localMoment.hour(),
      minute: localMoment.minute(),
      second: localMoment.second(),
      millisecond: localMoment.millisecond(),
      utcOffset: localMoment.utcOffset(),
      offsetString: localMoment.format('Z'),
      abbreviation: localMoment.format('z'),
      formatted: localMoment.format(),
      display: `${localMoment.format('YYYY-MM-DD HH:mm:ss')} (Local fallback)`,
      dateKey: dateKey(localMoment.year(), localMoment.month(), localMoment.date())
    }
  }
}

/**
 * Returns Y/M/D parts for "today" in a given timezone using Moment.js
 */
export const getTodayPartsInTimeZone = (timeZone: string = DEFAULT_CANADA_TZ): TimeZoneDateParts => {
  try {
    const momentTz = moment().tz(timeZone)

    return {
      year: momentTz.year(),
      monthIndex: momentTz.month(), // 0-11
      day: momentTz.date()
    }
  } catch (error) {
    const local = moment()
    return {
      year: local.year(),
      monthIndex: local.month(),
      day: local.date()
    }
  }
}

/** Build a sortable numeric key (YYYYMMDD). */
export const dateKey = (year: number, monthIndex: number, day: number) => year * 10000 + (monthIndex + 1) * 100 + day

/** Compare two date keys like compare functions (-1,0,1). */
export const compareDateKeys = (a: number, b: number) => (a < b ? -1 : a > b ? 1 : 0)

/** Parse a DD/MM/YYYY string into parts; returns null if invalid. */
export const parseDDMMYYYY = (value: string): TimeZoneDateParts | null => {
  const parts = value.split('/')
  if (parts.length !== 3) return null
  const [dStr, mStr, yStr] = parts
  const day = parseInt(dStr, 10)
  const month = parseInt(mStr, 10)
  const year = parseInt(yStr, 10)
  if (Number.isNaN(day) || Number.isNaN(month) || Number.isNaN(year)) return null
  if (month < 1 || month > 12 || day < 1 || day > 31) return null
  return { year, monthIndex: month - 1, day }
}

/** Format parts as DD/MM/YYYY */
export const formatDDMMYYYY = (year: number, monthIndex: number, day: number) =>
  `${String(day).padStart(2, '0')}/${String(monthIndex + 1).padStart(2, '0')}/${year}`

// --- Extended Canada (Toronto) time helper ---
export interface CanadaTorontoNow {
  year: number
  monthIndex: number
  day: number
  hour: number
  minute: number
  second: number
  offsetMinutes: number // e.g. -240 for EDT, -300 for EST
  offset: string        // "+/-HH:MM"
  isoLikeLocal: string  // YYYY-MM-DDTHH:mm:ss (Toronto wall time, no offset suffix)
  display: string       // Readable string
  dateKey: number       // YYYYMMDD numeric
}

/** Get current wall-clock time in America/Toronto with offset information. */
export const getCanadaTorontoNow = (): CanadaTorontoNow => {
  const tz = DEFAULT_CANADA_TZ
  const now = new Date()
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false, timeZoneName: 'short'
  })
  const parts = formatter.formatToParts(now)
  const pick = (t: string) => parts.find(p => p.type === t)?.value || ''
  const year = Number(pick('year'))
  const month = Number(pick('month'))
  const day = Number(pick('day'))
  const hour = Number(pick('hour'))
  const minute = Number(pick('minute'))
  const second = Number(pick('second'))
  const tzName = pick('timeZoneName') // e.g. EDT, EST, GMT-4

  // Derive offset minutes. Try to parse patterns like GMT-4 or UTC-5 first.
  let offsetMinutes = 0
  const gmtMatch = tzName.match(/([+-]?)(\d{1,2})(?::?(\d{2}))?/) // captures hours & optional minutes
  if (gmtMatch) {
    const sign = gmtMatch[1] === '-' ? -1 : 1
    const h = Number(gmtMatch[2])
    const m = gmtMatch[3] ? Number(gmtMatch[3]) : 0
    offsetMinutes = sign * (h * 60 + m)
  } else if (/EDT/i.test(tzName)) {
    offsetMinutes = -4 * 60
  } else if (/EST/i.test(tzName)) {
    offsetMinutes = -5 * 60
  }

  const sign = offsetMinutes <= 0 ? '+' : '-' // Note: offsetMinutes is negative for zones west of UTC
  const abs = Math.abs(offsetMinutes)
  const offH = String(Math.floor(abs / 60)).padStart(2, '0')
  const offM = String(abs % 60).padStart(2, '0')
  const offset = `${sign}${offH}:${offM}`

  const isoLikeLocal = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}`
  const display = `${isoLikeLocal} ${offset} (${tzName})`

  return {
    year,
    monthIndex: month - 1,
    day,
    hour,
    minute,
    second,
    offsetMinutes,
    offset,
    isoLikeLocal,
    display,
    dateKey: dateKey(year, month - 1, day)
  }
}
